// PWAUpdateToast — a bottom-sticky banner that appears when a new SW is
// ready. Tapping "套用更新" triggers the swap + reload. The toast can be
// dismissed (X) and will re-appear on the next fresh load.
//
// Mounted in App.tsx near OfflineIndicator. Listens for PWA_UPDATE_EVENT
// dispatched from registerSW.ts.

import { Han, T, TextBtn, useReducedMotion } from "@goodbye/ui";
import { useEffect, useState } from "react";
import {
  applyPendingUpdate,
  hasPendingUpdate,
  PWA_UPDATE_EVENT,
} from "../registerSW";

export function PWAUpdateToast() {
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState<boolean>(() => hasPendingUpdate());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onUpdate = () => {
      setDismissed(false);
      setVisible(true);
    };
    window.addEventListener(PWA_UPDATE_EVENT, onUpdate);
    return () => window.removeEventListener(PWA_UPDATE_EVENT, onUpdate);
  }, []);

  if (!visible || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: 16,
        right: 16,
        bottom: "calc(108px + env(safe-area-inset-bottom, 0px))",
        zIndex: 20,
        background: T.paperSink,
        border: `1px solid ${T.ink200}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        padding: "12px 14px 12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        animation: reducedMotion
          ? "none"
          : "gb-toast-rise 360ms var(--ease-mist)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Han size={12} color={T.ink900} ls="0.2em">
          · 新 版 本 就 緒
        </Han>
        <Han size={10} color={T.ink500}>
          重整一下、就用最新的
        </Han>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <TextBtn
          onClick={() => applyPendingUpdate()}
          mono={false}
          size={12}
          ls="0.25em"
          framed
          style={{ padding: "8px 12px" }}
        >
          套 用
        </TextBtn>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="關閉"
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            fontFamily: T.mono,
            fontSize: 24,
            color: T.ink500,
            lineHeight: 1,
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            touchAction: "manipulation",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
